import { Box } from '@mui/system';
import { FormEditorCard } from './FormEditorCard';
import FormEditor from './FormEditor/FormEditor';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  fieldTemplatesState,
  formTemplateState,
  formTemplateVersionsState,
  isFormSavingAtom,
  layoutSelector,
  selectedFormTemplateTypeState,
} from 'state';
import { useParams } from 'react-router-dom';
import { FormTemplate } from 'api/models';
import { GeneralIds, ModelTypes } from 'types/types';
import useQuery from 'hooks/useQuery';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { CircularProgress, LinearProgress } from '@mui/material';
import { getSampleData } from 'fixtures/sample-fixtures';

export enum FormTemplateEditorMode {
  default = 'default',
  sample = 'sample',
  report = 'report',
}

interface FormTemplateEditorProps {
  mode: FormTemplateEditorMode;
  viewOnly?: boolean;
}

export default function FormTemplateEditor({
  mode,
  viewOnly = false,
}: FormTemplateEditorProps) {
  const formTemplateType = useRecoilValue(selectedFormTemplateTypeState);
  const { projectId, formTemplateTypeId } = useParams<GeneralIds>();
  const setFormTemplateVersions = useSetRecoilState(formTemplateVersionsState);
  const setLayout = useSetRecoilState(layoutSelector);
  const setFieldTemplates = useSetRecoilState(fieldTemplatesState);
  const setFormTemplate = useSetRecoilState(formTemplateState);
  const isFormSaving = useRecoilValue(isFormSavingAtom);

  // Need to use state to trigger re-render on window resize because
  // the layout field column resize is not triggering a on window resize event
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const handleResize = () => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  const debouncedResizeEnd = useDebouncedCallback(handleResize, 300);

  useEffect(() => {
    window.addEventListener('resize', debouncedResizeEnd);
    return () => window.removeEventListener('resize', handleResize);
  }, [debouncedResizeEnd]);

  const { loading } = useQuery<FormTemplate[]>({
    type: ModelTypes.formTemplate,
    ids: {
      projectId,
      formTemplateTypeId,
    },
    set: {
      data: async data => {
        setFormTemplateVersions([...data]);
        const formTemplate = data[data.length - 1];
        if (formTemplate) {
          setLayout({
            children: formTemplate.layout.items ?? [],
          });
          setFieldTemplates(formTemplate.fieldTemplates ?? []);

          setFormTemplate(formTemplate);
        } else {
          setFormTemplate(undefined);

          const { items, fields } = await getSampleData({
            projectId: projectId!,
          });

          setLayout({
            children: mode === FormTemplateEditorMode.sample ? items : [],
          });
          setFieldTemplates(
            mode === FormTemplateEditorMode.sample ? fields : [],
          );
        }
      },
    },
  });

  if (loading) {
    return (
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        pt={15}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box pt={2} position={'relative'} key={`${windowSize.width}`}>
      {isFormSaving && (
        <LinearProgress
          sx={{
            position: 'absolute',
            width: '110%',
            left: '-17px',
            top: '1px',
          }}
        />
      )}
      <FormEditorCard
        formTemplateType={formTemplateType}
        isViewOnly={viewOnly}
      />
      <FormEditor mode={mode} isViewOnly={viewOnly} />
    </Box>
  );
}
